
import { useState, useEffect, useCallback, useRef } from 'react';
import { userStorage } from '@/lib/user-storage';
import { logger } from '@lark-apaas/client-toolkit-lite';

const STORAGE_KEY = 'daily_reminder_settings';

export interface IDailyReminderSettings {
  /** 总开关 */
  enabled: boolean;
  /** 训练日训练提醒时间（HH:mm），默认 08:00 */
  workoutReminderTime: string;
  /** 晚间打卡提醒时间（HH:mm），默认 20:00 */
  eveningReminderTime: string;
  /** 浏览器通知权限是否已授予（仅记录，实际以 Notification.permission 为准） */
  notificationGranted: boolean;
}

export const DEFAULT_REMINDER_SETTINGS: IDailyReminderSettings = {
  enabled: false,
  workoutReminderTime: '08:00',
  eveningReminderTime: '20:00',
  notificationGranted: false,
};

/**
 * 每日提醒设置 hook
 * 支持浏览器通知（Notification API），通过页面 setInterval 轮询触发
 * 不依赖 Service Worker，保持纯前端实现
 */
export function useDailyReminder() {
  const [settings, setSettings] = useState<IDailyReminderSettings>(() => {
    try {
      const raw = userStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        return { ...DEFAULT_REMINDER_SETTINGS, ...parsed };
      }
    } catch {
      // ignore
    }
    return DEFAULT_REMINDER_SETTINGS;
  });

  const lastFireRef = useRef<Record<string, string>>({});

  // 持久化
  useEffect(() => {
    userStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  }, [settings]);

  // 同步 Notification 权限状态
  useEffect(() => {
    if (typeof Notification === 'undefined') return;
    const granted = Notification.permission === 'granted';
    if (granted !== settings.notificationGranted) {
      setSettings(prev => ({ ...prev, notificationGranted: granted }));
    }
  }, [settings.notificationGranted]);

  const updateSetting = useCallback(<K extends keyof IDailyReminderSettings>(
    key: K,
    value: IDailyReminderSettings[K],
  ) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  }, []);

  /**
   * 请求浏览器通知权限
   */
  const requestNotificationPermission = useCallback(async (): Promise<boolean> => {
    if (typeof Notification === 'undefined') {
      logger.info('Notification API not supported');
      return false;
    }
    if (Notification.permission === 'granted') {
      setSettings(prev => ({ ...prev, notificationGranted: true }));
      return true;
    }
    if (Notification.permission === 'denied') {
      return false;
    }
    try {
      const result = await Notification.requestPermission();
      const granted = result === 'granted';
      setSettings(prev => ({ ...prev, notificationGranted: granted }));
      return granted;
    } catch (err) {
      logger.error('Notification request failed:', String(err));
      return false;
    }
  }, []);

  /**
   * 发送浏览器通知
   */
  const sendNotification = useCallback((title: string, body: string, tag?: string) => {
    if (typeof Notification === 'undefined') return;
    if (Notification.permission !== 'granted') return;
    try {
      // 关闭同 tag 的旧通知，避免堆叠
      if (tag) {
        // 静态方式不维护通知列表，浏览器会自动按 tag 替换
      }
      new Notification(title, {
        body,
        tag: tag || 'lianke-daily',
        icon: '/favicon.svg',
        badge: '/favicon.svg',
      });
    } catch (err) {
      logger.error('Notification send failed:', String(err));
    }
  }, []);

  /**
   * 启用每日提醒：请求权限 + 开启开关
   */
  const enableReminders = useCallback(async (): Promise<boolean> => {
    const granted = await requestNotificationPermission();
    // 即使没授权，也打开开关（降级为仅站内待办）
    setSettings(prev => ({ ...prev, enabled: true, notificationGranted: granted }));
    return granted;
  }, [requestNotificationPermission]);

  /**
   * 关闭每日提醒
   */
  const disableReminders = useCallback(() => {
    setSettings(prev => ({ ...prev, enabled: false }));
  }, []);

  /**
   * 定时检查是否该触发提醒（在首页 useEffect 中调用启动轮询）
   * 返回清理函数
   * @param getWorkoutInfo 返回 { isTrainingDay, workoutLabel, hasCompletedWorkout, hasCompletedDiet, hasCompletedHealth }
   */
  const startReminderLoop = useCallback((
    getWorkoutInfo: () => {
      isTrainingDay: boolean;
      workoutLabel: string;
      hasCompletedWorkout: boolean;
      hasCompletedDiet: boolean;
      hasCompletedHealth: boolean;
    },
  ) => {
    if (!settings.enabled) return () => {};

    const check = () => {
      const now = new Date();
      const nowStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      const todayStr = now.toDateString();

      const info = getWorkoutInfo();

      // 训练提醒：训练日 + 训练提醒时间 + 未完成训练
      if (
        info.isTrainingDay
        && nowStr === settings.workoutReminderTime
        && !info.hasCompletedWorkout
        && lastFireRef.current[`workout-${todayStr}`] !== settings.workoutReminderTime
      ) {
        lastFireRef.current[`workout-${todayStr}`] = settings.workoutReminderTime;
        sendNotification(
          '今日训练提醒',
          `今天该练${info.workoutLabel || '了'}，别忘记打卡`,
          `workout-${todayStr}`,
        );
      }

      // 晚间打卡提醒：晚间提醒时间 + (饮食未打卡 或 健康未打卡)
      if (
        nowStr === settings.eveningReminderTime
        && (!info.hasCompletedDiet || !info.hasCompletedHealth)
        && lastFireRef.current[`evening-${todayStr}`] !== settings.eveningReminderTime
      ) {
        lastFireRef.current[`evening-${todayStr}`] = settings.eveningReminderTime;
        const pending: string[] = [];
        if (!info.hasCompletedDiet) pending.push('饮食');
        if (!info.hasCompletedHealth) pending.push('健康');
        sendNotification(
          '晚间打卡提醒',
          `今日${pending.join('和')}打卡完成了吗？点此记录`,
          `evening-${todayStr}`,
        );
      }
    };

    // 每 30 秒检查一次（时间精度 ±30s）
    const timer = setInterval(check, 30 * 1000);
    // 立即执行一次
    check();

    return () => clearInterval(timer);
  }, [settings.enabled, settings.workoutReminderTime, settings.eveningReminderTime, sendNotification]);

  return {
    settings,
    updateSetting,
    requestNotificationPermission,
    sendNotification,
    enableReminders,
    disableReminders,
    startReminderLoop,
  };
}
